import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FloatButton, Spin } from "antd";
import {
  Loading3QuartersOutlined,
  CheckCircleFilled,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Input, Select, Button } from "@dreambigger/design-system";
import { api } from "@dreambigger/shared/src/api/acquire";
import CharitableOrganizationApi from "@dreambigger/shared/src/api/acquire/charitableOrganization";
import { CharitableOrganizationDto } from "@dreambigger/shared/src/types/charitable-organization";
import { TokenHelper } from "@dreambigger/shared/src/types";
import { FinancialBrand } from "@dreambigger/shared/src/types/acquire";
import {
  composeCharitableOrganizationSearchRequest,
  hasSearchableCriteria,
  resolveCauseOptions,
  resolveResultsPerPage,
  stateCodeOptions,
} from "../../utils/charitable-organization-search";

const spinnerIcon = <Loading3QuartersOutlined spin />;

type SearchStatus = "idle" | "loading" | "success" | "error";

// Design-system utility classes cover what maps to its scale (db/fwb/f-N/
// mb-N/flex/...); inline style carries only off-scale values (13/15px type,
// 6/10px gaps), palette grays, and dynamic brand colors. Form labels are
// bare <label> elements, matching the address step and CustomField.

// Iconed callout messages (hint / error / no-results). Blue = guidance,
// red = failure.
const calloutClass = "flex items-start mb-3";

const calloutBaseStyle: CSSProperties = {
  gap: 8,
  fontSize: 13,
  color: "#374151",
  borderRadius: 6,
  padding: "8px 12px",
};

const infoCalloutStyle: CSSProperties = {
  ...calloutBaseStyle,
  background: "#f0f5ff",
  border: "1px solid #d6e4ff",
};

const errorCalloutStyle: CSSProperties = {
  ...calloutBaseStyle,
  background: "#fff2f0",
  border: "1px solid #ffccc7",
};

const infoCalloutIcon = (
  <InfoCircleOutlined
    style={{ color: "#2f54eb", fontSize: 14, marginTop: 2 }}
  />
);

const chipClass = "dib f-2";

const chipStyle: CSSProperties = {
  background: "#eef2ff",
  color: "#4f46e5",
  borderRadius: 12,
  padding: "2px 10px",
  lineHeight: "20px",
  whiteSpace: "nowrap",
};

// Muted "+N" overflow chip shown when an org has more causes than we display.
const overflowChipStyle: CSSProperties = {
  ...chipStyle,
  background: "#f3f4f6",
  color: "#6b7280",
};

// Cap the cause chips per card so tag-heavy orgs can't stretch the card;
// the rest collapse into a "+N" chip.
const MAX_CHIPS_PER_CARD = 3;

// The API returns webUrl with or without a scheme ("www.example.org").
const websiteHref = (url: string) =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

// Link label: the bare domain (a verification signal — "is this my org?").
// Vendor URLs vary in hygiene, so fall back to a generic label when the
// value doesn't parse as a URL.
const websiteLabel = (url: string) => {
  try {
    return new URL(websiteHref(url)).hostname.replace(/^www\./i, "");
  } catch {
    return "Visit website";
  }
};

const cardClass = "db w-100 tl br-2 mb-3 pointer overflow-hidden";

const cardBaseStyle: CSSProperties = {
  background: "#fff",
  padding: "14px 18px",
};

export interface CharitableOrganizationSearchProps {
  flowId: string;
  tokenHelper: TokenHelper;
  brand: FinancialBrand;
  /** Configurable content (HTML supported) for zero-result searches. */
  noResultsText?: string;
  /** Results per request (step asset); clamped to 1–50, defaults to 24. */
  resultsPerPage?: number;
  /** Cause filter overrides (step asset); falls back to the built-in list. */
  causeOptions?: { label: string; value: string }[];
  /** The currently selected organization (owned by the host step). */
  selectedOrg: CharitableOrganizationDto | null;
  onSelect: (org: CharitableOrganizationDto | null) => void;
}

/**
 * Submit-driven charitable organization search: criteria in, explicit
 * Search, relevance-ranked results in an infinite-scroll list, single
 * selection reported to the host via onSelect. The host owns the selected
 * value (persistence and prefill are the step's job).
 */
export default function CharitableOrganizationSearch({
  flowId,
  tokenHelper,
  brand,
  noResultsText,
  resultsPerPage,
  causeOptions,
  selectedOrg,
  onSelect,
}: CharitableOrganizationSearchProps) {
  const pageSize = resolveResultsPerPage(resultsPerPage);
  const causeSelectOptions = resolveCauseOptions(causeOptions);
  const [query, setQuery] = useState("");
  const [cause, setCause] = useState<string | undefined>();
  const [city, setCity] = useState("");
  const [state, setState] = useState<string | undefined>();

  const [status, setStatus] = useState<SearchStatus>("idle");
  // What triggered the in-flight request, so only the control that fired it
  // reacts (loading more must not put the Search button into a loading state).
  const [loadingAction, setLoadingAction] =
    useState<"search" | "more" | null>(null);
  const [results, setResults] = useState<CharitableOrganizationDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [hint, setHint] = useState<string | null>(null);
  // Set when a "load more" page comes back short/empty — the vendor's total
  // is an estimate (capped at 10,000), so trust the data over the math.
  const [exhausted, setExhausted] = useState(false);
  const [loadMoreErrored, setLoadMoreErrored] = useState(false);
  // Cards whose full cause-chip list is expanded (via the "+N" chip).
  const [expandedChipCards, setExpandedChipCards] = useState<Set<string>>(
    new Set()
  );

  const resultsHeadingRef = useRef<HTMLParagraphElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  // Monotonic id so a response from a superseded request (older search /
  // auto-load, or one made before Clear) is dropped instead of overwriting
  // newer state.
  const requestIdRef = useRef(0);
  // Criteria of the last SUCCESSFUL search — re-running an identical search
  // is a no-op (the results are already on screen), so spamming the Search
  // button can't generate repeat API calls. Errors don't set it (retrying
  // the same criteria after a failure must work).
  const lastSearchKeyRef = useRef<string | null>(null);

  // Size the scrollable list to the space actually remaining in the viewport
  // (below the form, above the step's NEXT button), so the page itself never
  // needs to scroll — on any screen size. Reserve just enough below the list
  // for NEXT; the list absorbs everything else. The floor is deliberately
  // modest — a larger floor pushes NEXT below the fold on short screens.
  const FOOTER_RESERVE_PX = 110;
  const MIN_LIST_HEIGHT_PX = 200;
  const [listMaxHeight, setListMaxHeight] = useState<number>();

  const sizeList = useCallback(() => {
    if (!listRef.current) {
      return;
    }
    const top = listRef.current.getBoundingClientRect().top;
    setListMaxHeight(
      Math.max(MIN_LIST_HEIGHT_PX, window.innerHeight - top - FOOTER_RESERVE_PX)
    );
  }, []);

  // Re-measure when the layout above the list changes.
  useEffect(sizeList, [sizeList, status, results, selectedOrg]);

  // One mount-only resize listener, throttled to animation frames.
  useEffect(() => {
    let frame: number | null = null;
    const onResize = () => {
      if (frame !== null) {
        return;
      }
      frame = window.requestAnimationFrame(() => {
        frame = null;
        sizeList();
      });
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [sizeList]);

  const charitableOrganizationApi = useMemo(
    () => new CharitableOrganizationApi(api, tokenHelper),
    [tokenHelper]
  );

  const runSearch = (
    requestedPage: number,
    action: "search" | "more" = "search"
  ) => {
    // Single-flight: the buttons disable while loading, but Enter in the
    // text inputs doesn't — without this, mashing Enter fires a POST per
    // keypress.
    if (loadingAction) {
      return;
    }

    const criteria = {
      query,
      cause,
      city,
      state,
      page: requestedPage,
      limit: pageSize,
    };

    if (!hasSearchableCriteria(criteria)) {
      setHint(
        "Enter an organization name or keywords (at least 2 characters), or choose a filter."
      );
      return;
    }

    const searchKey = JSON.stringify([
      query.trim(),
      cause ?? "",
      city.trim(),
      state ?? "",
      pageSize,
    ]);
    if (
      action === "search" &&
      status === "success" &&
      lastSearchKeyRef.current === searchKey
    ) {
      // Identical to the search whose results are already on screen.
      return;
    }

    setHint(null);
    setLoadMoreErrored(false);
    setLoadingAction(action);

    if (action === "search") {
      setStatus("loading");
      // Deselect only on an actually-executed search (validation passed),
      // never on load-more — it extends the set the selection came from.
      if (selectedOrg) {
        onSelect(null);
      }
    }
    const requestId = ++requestIdRef.current;

    charitableOrganizationApi
      .searchOrganizations(
        flowId,
        composeCharitableOrganizationSearchRequest(criteria)
      )
      .then(({ data }) => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setTotal(data.total);
        setPage(data.page);
        setStatus("success");
        setLoadingAction(null);
        if (action === "search") {
          lastSearchKeyRef.current = searchKey;
        }

        if (action === "more") {
          // Append below what the donor is already looking at; scroll
          // position and chip/selection state stay put. Dedupe by org id —
          // the vendor's index can shift between page requests, so the same
          // org may appear on two pages (which would duplicate React keys).
          setResults((previous) => {
            const seen = new Set(
              previous.map((org) => String(org.organizationThriventId))
            );
            const fresh = data.results.filter(
              (org) => !seen.has(String(org.organizationThriventId))
            );
            return [...previous, ...fresh];
          });

          if (data.results.length < pageSize) {
            setExhausted(true);
          }
        } else {
          setResults(data.results);
          setExhausted(false);
          setExpandedChipCards(new Set());
          listRef.current?.scrollTo({ top: 0 });
          resultsHeadingRef.current?.focus();
        }
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setLoadingAction(null);
        if (action === "more") {
          // Keep the results the donor already has; error inline instead.
          setLoadMoreErrored(true);
        } else {
          setStatus("error");
        }
      });
  };

  const isSearching = loadingAction === "search";

  // A true start-over: criteria, results, AND selection. Keeping a selection
  // through Clear would be inconsistent with a mere re-search discarding it.
  const handleClear = () => {
    requestIdRef.current++; // orphan any in-flight request
    lastSearchKeyRef.current = null;
    setQuery("");
    setCause(undefined);
    setCity("");
    setState(undefined);
    setResults([]);
    setTotal(0);
    setPage(0);
    setHint(null);
    setStatus("idle");
    setLoadingAction(null);
    setExhausted(false);
    setLoadMoreErrored(false);
    onSelect(null);
  };

  const hasMore = !exhausted && results.length < total && results.length > 0;

  // Infinite scroll: a sentinel at the bottom of the list container triggers
  // the next page while the donor is still ~600px above it, so new cards are
  // usually rendered before they arrive — no clicking. The ref indirection
  // keeps the observer stable while the callback always sees fresh state
  // (single-flight + requestId still guard against duplicates).
  //
  // Cooldown: auto-loads are spaced at least LOAD_MORE_COOLDOWN_MS apart so
  // a free-spinning scroll wheel paces its request chain instead of loading
  // back-to-back at network speed. A fire during the cooldown schedules ONE
  // deferred re-check (otherwise the chain would stall — parked at the
  // bottom, no new intersection event ever comes). The re-check re-validates
  // all guards plus actual sentinel visibility at fire time.
  const LOAD_MORE_COOLDOWN_MS = 1000;
  const lastLoadMoreAtRef = useRef(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const sentinelVisibleRef = useRef(false);

  const loadMoreRef = useRef<() => void>();
  loadMoreRef.current = () => {
    if (
      !hasMore ||
      loadingAction ||
      status !== "success" ||
      loadMoreErrored ||
      !sentinelVisibleRef.current
    ) {
      return;
    }
    const elapsed = Date.now() - lastLoadMoreAtRef.current;
    if (elapsed < LOAD_MORE_COOLDOWN_MS) {
      if (cooldownTimerRef.current === undefined) {
        cooldownTimerRef.current = setTimeout(() => {
          cooldownTimerRef.current = undefined;
          loadMoreRef.current?.();
        }, LOAD_MORE_COOLDOWN_MS - elapsed);
      }
      return;
    }
    lastLoadMoreAtRef.current = Date.now();
    runSearch(page + 1, "more");
  };

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const rootEl = listRef.current;
    if (!sentinel || !rootEl) {
      sentinelVisibleRef.current = false;
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        sentinelVisibleRef.current = entries.some(
          (entry) => entry.isIntersecting
        );
        if (sentinelVisibleRef.current) {
          loadMoreRef.current?.();
        }
      },
      { root: rootEl, rootMargin: "600px 0px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [status, results.length, hasMore]);

  // Never leave a cooldown timer running after unmount.
  useEffect(
    () => () => {
      if (cooldownTimerRef.current !== undefined) {
        clearTimeout(cooldownTimerRef.current);
      }
    },
    []
  );
  const totalLabel = total >= 10000 ? "10,000+" : total.toLocaleString("en-US");

  const scrollListToTop = () => {
    listRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  // End-of-list marker: whenever the loaded list is complete. Even a single
  // page can scroll inside the bounded container, so donors need the signal
  // regardless of result count.
  const showEndOfResults = !hasMore && results.length > 0;

  const isSelected = (org: CharitableOrganizationDto) =>
    selectedOrg?.organizationThriventId != null &&
    String(selectedOrg.organizationThriventId) ===
      String(org.organizationThriventId);

  // An active selection must always be visible somewhere: as the highlighted
  // card when it's in the rendered list, otherwise as the summary block
  // (rehydrated selection before any search, after Clear, when a re-search
  // returned different orgs / no results, or in the error state). Exactly
  // one of the two — never zero, never both.
  const listRendered =
    (status === "success" || status === "loading") && results.length > 0;
  const selectedOrgInList =
    !!selectedOrg && listRendered && results.some(isSelected);
  const showSelectedSummary = !!selectedOrg && !selectedOrgInList;

  const renderCard = (org: CharitableOrganizationDto) => {
    const selected = isSelected(org);
    // Full address: street (line1 + apartment/suite when the API provides
    // it, already folded into organizationAddress), city, state, zip.
    const location = [
      org.organizationAddress,
      org.organizationCity,
      org.organizationState,
    ]
      .filter(Boolean)
      .join(", ");
    const purpose = org.purpose;
    const orgId = String(org.organizationThriventId);
    const sortedFocuses = [...org.focuses].sort((a, b) =>
      a.categoryDescription.localeCompare(b.categoryDescription)
    );
    const chipsExpanded = expandedChipCards.has(orgId);
    const visibleFocuses = chipsExpanded
      ? sortedFocuses
      : sortedFocuses.slice(0, MAX_CHIPS_PER_CARD);
    const overflowCount = sortedFocuses.length - MAX_CHIPS_PER_CARD;
    const toggleChips = () => {
      setExpandedChipCards((previous) => {
        const next = new Set(previous);
        if (next.has(orgId)) {
          next.delete(orgId);
        } else {
          next.add(orgId);
        }
        return next;
      });
    };

    const toggleSelect = (card: HTMLElement) => {
      onSelect(selected ? null : org);
      // Clicking mid-momentum-scroll can leave the chosen card half out of
      // view (the list keeps coasting after the tap) — settle it into view.
      if (!selected) {
        card.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    };

    return (
      // div role="button" rather than <button>: the card contains a real
      // website <a>, and nested interactive elements inside a button are
      // invalid HTML.
      <div
        key={orgId}
        className={cardClass}
        role="button"
        tabIndex={0}
        onClick={(e) => toggleSelect(e.currentTarget)}
        onKeyDown={(e) => {
          // Only when the card itself is focused — Enter on the nested
          // website link must navigate, not select.
          if (
            e.target === e.currentTarget &&
            (e.key === "Enter" || e.key === " ")
          ) {
            e.preventDefault();
            toggleSelect(e.currentTarget);
          }
        }}
        aria-pressed={selected}
        style={{
          ...cardBaseStyle,
          border: selected
            ? `1.5px solid ${brand.primaryColor}`
            : "1px solid #e5e7eb",
        }}
      >
        <div className="flex justify-between items-start" style={{ gap: 12 }}>
          <span className="fwb" style={{ fontSize: 15 }}>
            {selected && (
              <CheckCircleFilled
                style={{
                  fontSize: 15,
                  paddingRight: 8,
                  color: brand.primaryColor,
                }}
              />
            )}
            {org.organizationName}
          </span>
          {visibleFocuses.length > 0 && (
            <span
              className="flex flex-wrap justify-end"
              style={{ gap: 6, maxWidth: "45%" }}
            >
              {visibleFocuses.map((focus) => (
                <span
                  key={focus.categoryCode}
                  className={chipClass}
                  style={chipStyle}
                >
                  {focus.categoryDescription}
                </span>
              ))}
              {overflowCount > 0 && (
                // Expands/collapses this card's chips without selecting the
                // card (the card itself is a button, so stop the bubble).
                <span
                  role="button"
                  tabIndex={0}
                  className={`${chipClass} pointer`}
                  style={overflowChipStyle}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleChips();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation(); // don't also select the card
                      toggleChips();
                    }
                  }}
                  aria-expanded={chipsExpanded}
                  aria-label={
                    chipsExpanded
                      ? "Show fewer causes"
                      : `Show ${overflowCount} more causes`
                  }
                >
                  {chipsExpanded ? "show less" : `+${overflowCount}`}
                </span>
              )}
            </span>
          )}
        </div>
        {purpose && (
          <p
            style={{
              fontSize: 13,
              color: "#4b5563",
              lineHeight: "20px",
              margin: "6px 0 0",
            }}
          >
            {purpose}
          </p>
        )}
        {(location || org.organizationZip) && (
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
            <EnvironmentOutlined style={{ marginRight: 6 }} />
            {location} {org.organizationZip ?? ""}
          </div>
        )}
        {org.webUrl && (
          <div style={{ fontSize: 13, marginTop: 6 }}>
            <GlobalOutlined style={{ marginRight: 6, color: "#6b7280" }} />
            <a
              href={websiteHref(org.webUrl)}
              target="_blank"
              rel="noopener noreferrer"
              // Visiting the website must not select the card.
              onClick={(e) => e.stopPropagation()}
              aria-label={`Visit ${org.organizationName} website (opens in a new tab)`}
              className="dib overflow-hidden"
              style={{
                maxWidth: "100%",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                verticalAlign: "bottom",
              }}
            >
              {websiteLabel(org.webUrl)}
            </a>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: "100%" }}>
      {/* --- Search criteria --- */}
      <div style={{ marginBottom: 10 }}>
        <label htmlFor="org-search-query">Search</label>
        <Input
          id="org-search-query"
          className="w-100 ao-bl-input"
          placeholder="Organization name or keywords"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          // preventDefault: these inputs render inside the step's <Form>, so
          // an unhandled Enter would ALSO submit the step (advancing to the
          // next step once an org is selected).
          onPressEnter={(e) => {
            e.preventDefault();
            runSearch(0);
          }}
          allowClear
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label htmlFor="org-search-cause">Cause</label>
        <Select
          id="org-search-cause"
          className="w-100 ao-bl-select"
          placeholder="All Causes"
          value={cause}
          onChange={(value: string | undefined) => setCause(value)}
          options={causeSelectOptions}
          allowClear
          showSearch
          optionFilterProp="label"
        />
      </div>

      <div className="flex mb-3" style={{ gap: 16 }}>
        <div className="flex-1" style={{ minWidth: 0 }}>
          <label htmlFor="org-search-city">City</label>
          <Input
            id="org-search-city"
            className="w-100 ao-bl-input"
            placeholder="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            // preventDefault: see the search input above.
            onPressEnter={(e) => {
              e.preventDefault();
              runSearch(0);
            }}
            allowClear
          />
        </div>
        <div className="flex-1" style={{ minWidth: 0 }}>
          <label htmlFor="org-search-state">State</label>
          <Select
            id="org-search-state"
            className="w-100 ao-bl-select"
            placeholder="State"
            value={state}
            onChange={(value: string | undefined) => setState(value)}
            options={stateCodeOptions}
            allowClear
            showSearch
            optionFilterProp="label"
          />
        </div>
      </div>

      {/* Asymmetric spacing: tight above (the button belongs to the form),
          generous below (the results are a separate zone) — otherwise the
          button reads as sandwiched between two equal slabs. */}
      <div
        className="flex items-center"
        style={{ gap: 8, margin: "16px 0 24px" }}
      >
        <Button
          type="primary"
          size="middle"
          // loading alone blocks clicks (runSearch single-flights as backstop);
          // adding disabled would trigger the design-system's gray disabled
          // paint over the branded button.
          loading={isSearching}
          // Magnifier distinguishes Search from the step's NEXT pill (which
          // carries the forward arrow) — same weight, different verbs.
          icon={<SearchOutlined />}
          onClick={() => runSearch(0)}
        >
          Search
        </Button>
        {/* Quiet, conditional reset: never competes with the primary
            action, and never renders as a dead button. */}
        {(query || cause || city || state || status !== "idle") && (
          <Button
            // type="link": text buttons get antd's hover background, which
            // the design-system's pill radius renders as a gray blob on a
            // button this small. Hover feedback is the h_ text-darken instead.
            // Never disabled: the design-system paints disabled buttons with
            // a gray background (same blob), and clearing mid-search is safe
            // anyway — handleClear orphans the in-flight request, so this
            // doubles as "cancel search".
            type="link"
            size="middle"
            className="h_gray-8"
            onClick={handleClear}
            style={{ color: "#6b7280" }}
          >
            Clear
          </Button>
        )}
      </div>

      {hint && (
        <p role="alert" className={calloutClass} style={infoCalloutStyle}>
          {infoCalloutIcon}
          {hint}
        </p>
      )}

      {/* Divider between the search area and the results zone. Only once
          settled content exists below it — during the FIRST search's
          spinner (loading with nothing yet) a lone divider would flash in
          before the results repaint. */}
      {(status === "success" ||
        status === "error" ||
        (status === "loading" && results.length > 0) ||
        showSelectedSummary) && (
        <div style={{ borderTop: "1px solid #e5e7eb", margin: "0 0 12px" }} />
      )}

      {/* --- Selected organization summary --- see showSelectedSummary. */}
      {showSelectedSummary && selectedOrg && (
        <div
          className="br-2 mb-4"
          style={{
            border: `1.5px solid ${brand.primaryColor}`,
            padding: "12px 18px",
          }}
        >
          <div className="fwb mb-1" style={{ fontSize: 13, color: "#6b7280" }}>
            Selected organization
          </div>
          <div className="fwb" style={{ fontSize: 15 }}>
            <CheckCircleFilled
              style={{
                fontSize: 15,
                paddingRight: 8,
                color: brand.primaryColor,
              }}
            />
            {selectedOrg.organizationName}
          </div>
          {(selectedOrg.organizationAddress ||
            selectedOrg.organizationCity ||
            selectedOrg.organizationState) && (
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
              <EnvironmentOutlined style={{ marginRight: 6 }} />
              {[
                selectedOrg.organizationAddress,
                selectedOrg.organizationCity,
                selectedOrg.organizationState,
              ]
                .filter(Boolean)
                .join(", ")}{" "}
              {selectedOrg.organizationZip ?? ""}
            </div>
          )}
          <Button
            type="link"
            size="small"
            // Explicit color/size: the flows app's global `a { color:
            // inherit }` otherwise leaves this looking like static text.
            style={{
              paddingLeft: 0,
              fontSize: 13,
              color: "#1677ff",
              marginTop: 6,
            }}
            onClick={() => onSelect(null)}
            aria-label="Clear selected organization"
          >
            Change selection
          </Button>
        </div>
      )}

      {/* --- Results region --- */}
      <div aria-live="polite">
        {/* Standalone spinner only for the FIRST load (nothing to show yet).
            Pagination/re-searches keep the list mounted and overlay it, so
            there is no layout collapse/repaint. */}
        {status === "loading" && results.length === 0 && (
          <div className="tc pv-5">
            <Spin indicator={spinnerIcon} />
          </div>
        )}

        {status === "error" && (
          <p role="alert" className={calloutClass} style={errorCalloutStyle}>
            <ExclamationCircleOutlined
              style={{ color: "#cf1322", fontSize: 14, marginTop: 2 }}
            />
            Unable to search organizations at this time. Please try again.
          </p>
        )}

        {status === "success" && results.length === 0 && (
          <div>
            {noResultsText ? (
              <div
                className="f-3"
                style={{ color: "#4b5563", lineHeight: "22px" }}
                dangerouslySetInnerHTML={{ __html: noResultsText }}
              />
            ) : (
              // Default copy gets the info-callout treatment (a valid outcome
              // with guidance — blue, not error red). Configured noResultsText
              // renders unwrapped above: the client owns that presentation.
              <p className={calloutClass} style={infoCalloutStyle}>
                {infoCalloutIcon}
                No organizations matched your search. Try different keywords or
                fewer filters.
              </p>
            )}
          </div>
        )}

        {(status === "success" || status === "loading") && results.length > 0 && (
          <>
            <p
              ref={resultsHeadingRef}
              tabIndex={-1}
              className="mb-3"
              style={{ fontSize: 13, color: "#6b7280", outline: "none" }}
            >
              {totalLabel} organization{total === 1 ? "" : "s"} found
            </p>
            {/* Bounded, internally-scrollable list sized to the remaining
                  viewport space so the step's NEXT button stays visible
                  without scrolling the whole page. While a new page loads,
                  the list stays mounted and Spin overlays it in place. */}
            {/* position: relative so the floating back-to-top button can
                anchor to the list's corner rather than the viewport. */}
            <div style={{ position: "relative" }}>
              <Spin spinning={status === "loading"} indicator={spinnerIcon}>
                <div
                  ref={listRef}
                  style={{
                    maxHeight: listMaxHeight ? `${listMaxHeight}px` : "40vh",
                    overflowY: "auto",
                    paddingRight: 4,
                    // Top padding keeps the fade zone over whitespace when
                    // scrolled to the top (no dimming of the first card).
                    paddingTop: 12,
                    // Edge fades: clipped cards dissolve at the container
                    // edges instead of being cut mid-content, and signal
                    // that the list continues.
                    WebkitMaskImage:
                      "linear-gradient(to bottom, transparent 0, black 16px, black calc(100% - 16px), transparent 100%)",
                    maskImage:
                      "linear-gradient(to bottom, transparent 0, black 16px, black calc(100% - 16px), transparent 100%)",
                  }}
                >
                  {results.map(renderCard)}
                  {hasMore && !loadMoreErrored && (
                    <>
                      {/* Invisible trigger for the IntersectionObserver. */}
                      <div ref={sentinelRef} style={{ height: 1 }} />
                      {loadingAction === "more" && (
                        <div
                          className="tc pv-2"
                          aria-label="Loading more organizations"
                        >
                          <Spin indicator={spinnerIcon} size="small" />
                        </div>
                      )}
                    </>
                  )}
                  {hasMore && loadMoreErrored && (
                    <p
                      style={{
                        fontSize: 13,
                        color: "#4b5563",
                      }}
                      className="tc pv-2"
                    >
                      Unable to load more organizations.{" "}
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0 }}
                        onClick={() => runSearch(page + 1, "more")}
                      >
                        Retry
                      </Button>
                    </p>
                  )}
                  {showEndOfResults && (
                    <p
                      className="tc pv-2 mb-0"
                      style={{ fontSize: 13, color: "#6b7280" }}
                    >
                      Showing all {results.length.toLocaleString("en-US")}{" "}
                      organizations ·{" "}
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0, fontSize: 13 }}
                        onClick={scrollListToTop}
                      >
                        Back to top
                      </Button>
                    </p>
                  )}
                </div>
              </Spin>
              {/* Floating back-to-top for deep scrollers who haven't reached
                  the end marker; drives the internal list, not the window.
                  Anchored BELOW the list (in the footer-reserve whitespace,
                  right of the step's left-aligned NEXT) so it never covers
                  card content. */}
              <FloatButton.BackTop
                target={() => listRef.current ?? window}
                visibilityHeight={600}
                // Labeled so the control is self-explanatory at a glance;
                // tooltip spells it out for hover users.
                shape="square"
                description="Top"
                tooltip="Back to top"
                style={{ position: "absolute", right: 4, bottom: -52 }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
